import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const FavoriteButton = ({ vendorId, size = 'md', showLabel = false, showTooltip = false }) => {
  const { user } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Check if user is authenticated - either via React state or token
  const token = localStorage.getItem('token');
  const isAuthenticated = !!(user || token);

  useEffect(() => {
    if (vendorId && isAuthenticated) {
      fetchStatus();
    }
    if (vendorId) {
      fetchCount();
    }
  }, [vendorId, isAuthenticated]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`/api/favorites/vendor/${vendorId}/check`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setIsFavorited(data.isFavorited || false);
      }
    } catch (error) {
      console.error('Error fetching favorite status:', error);
    }
  };

  const fetchCount = async () => {
    try {
      const res = await fetch(`/api/favorites/vendor/${vendorId}/count`);
      if (res.ok) {
        const data = await res.json();
        setCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching favorite count:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      alert('Please login to favorite vendors');
      return;
    }

    setLoading(true);
    try {
      const headers = getHeaders();
      if (isFavorited) {
        const res = await fetch(`/api/favorites/vendor/${vendorId}`, { method: 'DELETE', headers });
        if (res.ok) {
          setIsFavorited(false);
          setCount(prev => Math.max(0, prev - 1));
        }
      } else {
        const res = await fetch(`/api/favorites/vendor/${vendorId}`, { method: 'POST', headers });
        if (res.ok) {
          setIsFavorited(true);
          setCount(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Failed to update favorite. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const tooltipText = isFavorited 
    ? 'You are following this vendor. Click to unfollow and stop receiving updates.' 
    : 'Click to follow this vendor and receive updates in your Our Pickup inbox.';

  if (!vendorId) return null;

  return (
    <div className="relative inline-block">
      <button
        onClick={toggleFavorite}
        disabled={loading}
        className={`
          favorite-button inline-flex items-center gap-1.5 rounded-full transition-all
          ${isFavorited ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-50 text-gray-600 border-gray-200'}
          hover:bg-gray-100 cursor-pointer
          border ${sizeClasses[size]}
        `}
        aria-label={isFavorited ? 'Remove favorite' : 'Add favorite'}
        title={showTooltip ? tooltipText : ''}
      >
        <span className="favorite-icon text-lg">{isFavorited ? '❤️' : '🤍'}</span>
        {showLabel && <span className="favorite-label">{isFavorited ? 'Favorited' : 'Favorite'}</span>}
        <span className="favorite-count text-xs font-medium bg-white/50 px-1.5 py-0.5 rounded-full">
          {count}
        </span>
      </button>
    </div>
  );
};

export default FavoriteButton;
